import React, { useState } from "react";
import {
  getImageWithFallback,
  getPrimaryProductImageUrl,
} from "../../utils/imageUtils";
import { motion } from "framer-motion";
import type { ProductImageGalleryProps } from "./types";
import { useImageZoom } from "../../utils/imageUtils";

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  product,
  mainImage,
  onImageChange,
}) => {
  // Product images from image_paths
  const productImages =
    product && Array.isArray(product.image_paths)
      ? product.image_paths.map((img: string) =>
          getPrimaryProductImageUrl([img]),
        )
      : [];

  // Lightbox modal state
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const {
    containerRef,
    handleMouseMove,
    isHovered,
    setIsHovered,
    zoomPosition,
  } = useImageZoom();
  return (
    <>
      <div className="space-y-4">
        <div className="relative bg-section-surface rounded-xl border border-section-border p-8 overflow-hidden">
          {typeof product.sale_rate === "number" && product.sale_rate > 0 && (
            <div className="absolute top-2 right-2 z-10 flex gap-1">
              <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-bold shadow-lg">
                -{product.sale_rate}%
              </span>
            </div>
          )}

          {product.is_featured && (
            <div className="absolute top-4 left-4 z-10">
              <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                Featured
              </span>
            </div>
          )}

          <motion.div
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => setLightboxOpen(true)}
            className="aspect-square flex items-center justify-center md:cursor-crosshair cursor-pointer"
          >
            <img
              src={getImageWithFallback(mainImage)}
              alt={product.name || "Product"}
              className="max-w-full max-h-full object-contain pointer-events-none drop-shadow-xl"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.src = getImageWithFallback("");
              }}
            />
            {window.innerWidth >= 1024 && (
              <motion.div
                className="absolute inset-0 bg-section-surface pointer-events-none"
                style={{
                  backgroundImage: `url('${getImageWithFallback(mainImage)}')`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "150%",
                  backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.1 }}
              />
            )}
          </motion.div>
        </div>

        {/* Thumbnail Images - Scrollable Carousel */}
        {productImages.length > 0 && (
          <div className="relative">
            <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-2">
              {productImages.map((image: string, index: number) => (
                <button
                  key={index}
                  onClick={() => onImageChange(image)}
                  className={`shrink-0 w-20 h-20 bg-section-surface rounded-lg border-2 p-2 transition-all ${
                    mainImage === image
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-section-border hover:border-section-text"
                  }`}
                >
                  <img
                    src={getImageWithFallback(image)}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = getImageWithFallback("");
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={getImageWithFallback(mainImage)}
            alt={product.name || "Product"}
            className="max-w-[90vw] max-h-[90vh] object-contain drop-shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-6 right-6 text-white text-3xl bg-black/60 hover:bg-black/80 rounded-full w-12 h-12 flex items-center justify-center transition-colors"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};

export default ProductImageGallery;
